import { Component, inject, OnInit, CUSTOM_ELEMENTS_SCHEMA, OnDestroy } from '@angular/core';
import { IonMenu, MenuController, NavController, ModalController } from '@ionic/angular/standalone';
import { User } from 'src/app/core/models/User';
import { Subscription } from 'rxjs';

import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonText,
  IonNote,
  IonSkeletonText,
  IonIcon,
  IonSpinner,
} from '@ionic/angular/standalone';

import { addIcons } from 'ionicons';
import { refreshOutline } from 'ionicons/icons';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { UserRolesService } from 'src/app/core/services/auth/user-roles.service';
import { CharityFollowService } from 'src/app/core/services/charity/charity-follow.service';
import { CommonModule } from '@angular/common';
import { AvatarComponent } from '../avatar/avatar.component';
import { Charity } from 'src/app/core/models/Charity';
import { Business } from 'src/app/core/models/Business';
import { environment } from 'src/environments/environment';
import { CharitySelectorBottomSheetComponent } from '../charity-selector-bottom-sheet/charity-selector-bottom-sheet.component';
import { BusinessSelectorBottomSheetComponent } from 'src/app/role-business/components/business-selector-bottom-sheet/business-selector-bottom-sheet.component';
import { CreditsService } from 'src/app/core/services/credits/credits.service';
import { PwaUpdateService } from 'src/app/core/services/pwa/pwa-update.service';
import { EventTicketPurchaseWithEvent } from 'src/app/core/models/Event';
import { EventService } from 'src/app/core/services/events/event.service';
import { hasAnyEventTickets } from 'src/app/core/utilities/event-ticket-helpers';
import { PortalSwitchService } from 'src/app/core/services/navigation/portal-switch.service';
import { AlertService } from 'src/app/core/services/navigation/alert.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu-user.component.html',
  styleUrls: ['./menu-user.component.scss'],
  standalone: true,
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonText,
    IonNote,
    IonSkeletonText,
    IonIcon,
    IonSpinner,
    CommonModule,
    AvatarComponent,
    IonMenu
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MenuUserComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  private menuController = inject(MenuController);
  private modalController = inject(ModalController);
  private userRolesService = inject(UserRolesService);
  // Not private: the template calls `pwaUpdateService.isChecking()` directly to drive
  // the version-row refresh button's spinner/disabled state.
  protected readonly pwaUpdateService: PwaUpdateService = inject(PwaUpdateService);

  loggedInUser!: User;
  authorityLoaded: boolean = false;
  permittedCharities: Charity[] = [];
  permittedBusinesses: Business[] = [];
  following: string = "0";
  donations: string = "0";
  credits: string = "$0.00";
  hasEventTickets: boolean = false;
  authService: AuthService = inject(AuthService);
  avatarUrl: string = 'assets/ic-profile-avatar.svg';
  isSwitchingToCharity: boolean = false;
  isSwitchingToBusiness: boolean = false;
  
  appVersion: string = environment.appVersion || '';
  shouldShowVersion: boolean = true;

  constructor(
    private navController: NavController,
    private charityFollowService: CharityFollowService,
    private creditsService: CreditsService,
    private eventService: EventService,
    private portalSwitchService: PortalSwitchService,
    private alertService: AlertService,
  ) {
    addIcons({ refreshOutline });
  }

  ngOnInit() {
    this.subscribeToUserInfo();
    this.subscribeToRoles();
  }

  async checkForUpdates(): Promise<void> {
    await this.pwaUpdateService.checkForUpdate();
  }

  private subscribeToUserInfo(): void {
    this.subscriptions.add(
      this.authService.user$.subscribe((user: User | null) => {
        if (user) {
          this.loggedInUser = user;
          this.avatarUrl = user.avatarUrl ?? '';
          this.initializeFollowingCount();
          this.initializeCreditBalance();
          this.initializeEventTickets();

          const donations: string | null = sessionStorage.getItem('donationCount');
          if (donations) this.donations = donations;
        } else {
          this.hasEventTickets = false;
        }
      }),
    );

    this.subscriptions.add(
      this.charityFollowService.followingCount$.subscribe((count: number) => {
        this.following = count.toString();
      })
    );
  }

  private subscribeToRoles(): void {
    this.subscriptions.add(
      this.userRolesService.rolesLoaded$.subscribe((loaded: boolean) => {
        this.authorityLoaded = loaded;
      })
    );
    this.subscriptions.add(
      this.userRolesService.permittedCharities$.subscribe((charities: Charity[]) => {
        this.permittedCharities = charities;
      })
    );
    this.subscriptions.add(
      this.userRolesService.permittedBusinesses$.subscribe((businesses: Business[]) => {
        this.permittedBusinesses = businesses;
      })
    );
  }

  private initializeFollowingCount(): void {
    if (this.loggedInUser?.id) {
      this.subscriptions.add(
        this.charityFollowService.getFollowedCharitiesList(this.loggedInUser.id).subscribe({
          next: (followedCharities) => {
            this.following = followedCharities.length.toString();
          },
          error: (error) => {
            console.error('Error initializing following count:', error);
          }
        })
      );
    }
  }

  private initializeCreditBalance(): void {
    if (this.loggedInUser?.id) {
      this.subscriptions.add(
        this.creditsService.getUserBalance(this.loggedInUser.id).subscribe({
          next: (userCredits) => {
            const dollars: number = userCredits.credits / 100;
            this.credits = dollars.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          },
          error: (error) => {
            console.error('Error initializing credit balance:', error);
          }
        })
      );
    }
  }

  private initializeEventTickets(): void {
    this.subscriptions.add(
      this.eventService.getMyTickets().subscribe({
        next: (tickets: EventTicketPurchaseWithEvent[]) => {
          this.hasEventTickets = hasAnyEventTickets(tickets);
        },
        error: () => {
          this.hasEventTickets = false;
        }
      })
    );
  }

  async signOut(): Promise<void> {
    await this.authService.logout();
    this.menuController.close('app-menu');
    this.navController.navigateRoot('/welcome');
  }

  goToItem(item: string): void {
    this.menuController.close('app-menu');
    this.navController
      .navigateForward(['/main/' + item + '/' + this.loggedInUser?.id]);
  }

  goToPage(page: string): void {
    this.menuController.close('app-menu');
    this.navController.navigateForward(['/main/' + page]);
  }

  goToMyProfile(): void {
    if (!this.loggedInUser?.id) {
      return;
    }
    this.menuController.close('app-menu');
    this.navController.navigateForward(['/main/user', this.loggedInUser.id]);
  }

  async switchToBusiness(): Promise<void> {
    if (this.isSwitchingToBusiness) {
      return;
    }

    if (!this.permittedBusinesses || this.permittedBusinesses.length === 0) {
      return;
    }
    
    // If multiple businesses, show selector modal
    if (this.permittedBusinesses.length > 1) {
      const modal: HTMLIonModalElement = await this.modalController.create({
        component: BusinessSelectorBottomSheetComponent,
        cssClass: 'bottom-sheet-modal',
        componentProps: { 
          permittedBusinesses: this.permittedBusinesses,
          currentBusinessId: null
        },
        backdropDismiss: true,
      });
      
      modal.onDidDismiss().then((result) => {
        if (result.data?.selected) {
          this.menuController.close('app-menu');
        } 
      });
      
      await modal.present();
      return;
    }
    
    await this.performBusinessSwitch();
  }

  private async performBusinessSwitch(): Promise<void> {
    this.isSwitchingToBusiness = true;
    try {
      const switched: boolean = await this.portalSwitchService.switchToBusiness(this.permittedBusinesses[0].id, async () => {
        await this.menuController.close('app-menu');
      });
      if (!switched) {
        await this.menuController.close('app-menu');
        this.alertService.showToast("Couldn't switch portals. Please try again.", 'danger');
      }
    } finally {
      this.isSwitchingToBusiness = false;
    }
  }

  async switchToCharity(): Promise<void> {
    if (this.isSwitchingToCharity) {
      return;
    }

    if (!this.permittedCharities || this.permittedCharities.length === 0) {
      return;
    }

    await this.performCharitySwitch();
  }

  private async performCharitySwitch(): Promise<void> {
    if (this.permittedCharities.length > 1) {
      const modal: HTMLIonModalElement = await this.modalController.create({
        component: CharitySelectorBottomSheetComponent,
        cssClass: 'bottom-sheet-modal',
        componentProps: { 
          permittedCharities: this.permittedCharities,
          currentCharityId: sessionStorage.getItem('currentCharityId') || sessionStorage.getItem('charityId')
        },
        backdropDismiss: true,
      });
      
      modal.onDidDismiss().then((result) => {
        if (result.data?.selected) {
          this.menuController.close('app-menu');
        } 
      });
      
      await modal.present();
      return;
    }

    this.isSwitchingToCharity = true;
    try {
      const switched: boolean = await this.portalSwitchService.switchToCharity(this.permittedCharities[0].id, async () => {
        await this.menuController.close('app-menu');
      });
      if (!switched) {
        await this.menuController.close('app-menu');
        this.alertService.showToast("Couldn't switch portals. Please try again.", 'danger');
      }
    } finally {
      this.isSwitchingToCharity = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
